/*
 * <allergen-info>
 *
 * Renders the per-product ingredients & allergens on the PDP from the
 * precomputed custom.allergen_info metafield (see bom/9_write_dietary.py):
 *  - an always-visible "Contains: …" summary in the dietary box, and
 *  - a "View full ingredient breakdown" popup listing each item.
 *
 * A per-variant map is embedded once (script[data-allergen-map], keyed by
 * variant id). On variant change we re-render the summary + popup for the
 * selected variant so a Steak vs Cheese & Onion pasty shows the correct
 * allergens. No app, no fetch — all data is inline.
 */
if (!customElements.get('allergen-info')) {
  const esc = (s) =>
    String(s == null ? '' : s).replace(/[&<>"]/g, (c) =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c])
    );

  customElements.define(
    'allergen-info',
    class AllergenInfo extends HTMLElement {
      connectedCallback() {
        this.dialog = this.querySelector('dialog');
        this.summaryEl = this.querySelector('[data-allergen-summary]');
        this.itemsEl = this.querySelector('[data-allergen-items]');
        this.sectionId = this.dataset.sectionId;

        const mapEl = this.querySelector('[data-allergen-map]');
        try {
          this.map = JSON.parse(mapEl.textContent);
        } catch (e) {
          this.map = {};
        }

        const trigger = this.querySelector('[data-allergen-open]');
        if (trigger && this.dialog) {
          trigger.addEventListener('click', () => {
            if (typeof this.dialog.showModal === 'function') this.dialog.showModal();
            else this.dialog.setAttribute('open', '');
          });
        }
        this.querySelectorAll('[data-allergen-close]').forEach((b) =>
          b.addEventListener('click', () => this.dialog && this.dialog.close())
        );
        if (this.dialog) {
          this.dialog.addEventListener('click', (e) => {
            if (e.target === this.dialog) this.dialog.close();
          });
        }

        // Re-render for the current variant (in case it isn't the first one).
        const id = this._currentVariantId();
        if (id && this.map[id]) this.render(this.map[id]);

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsub = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            const variant = event && event.data && event.data.variant;
            if (!variant) return;
            if (this.sectionId && event.data.sectionId && event.data.sectionId !== this.sectionId) return;
            const info = this.map[variant.id];
            if (info) this.render(info);
          });
        }
      }

      disconnectedCallback() {
        if (this.unsub) this.unsub();
      }

      _currentVariantId() {
        const form = document.getElementById('product-form-' + this.sectionId);
        const input = form && form.querySelector('input[name="id"]');
        return input ? input.value : null;
      }

      render(info) {
        if (!info) return;
        if (this.summaryEl) {
          const allergens = info.allergens || [];
          let html = allergens.length
            ? '<strong>Contains:</strong> ' + allergens.map(esc).join(', ')
            : 'No major allergens declared.';
          if (info.incomplete) {
            html +=
              '<span class="allergen-info__note">Full details for every item are still being added; please check the packaging on arrival.</span>';
          }
          this.summaryEl.innerHTML = html;
        }
        if (this.itemsEl) {
          this.itemsEl.innerHTML = (info.items || [])
            .map((it) => {
              const qty = it.qty && it.qty !== 1 ? ' &times;' + it.qty : '';
              const al =
                it.allergens && it.allergens.length ? it.allergens.map(esc).join(', ') : 'Not yet listed';
              const ing = it.ingredients ? esc(it.ingredients) : 'See packaging';
              return (
                '<div class="allergen-info__item">' +
                '<h3 class="allergen-info__name h5">' + esc(it.name) + qty + '</h3>' +
                '<p class="allergen-info__row"><strong>Allergens:</strong> ' + al + '</p>' +
                '<p class="allergen-info__row"><strong>Ingredients:</strong> ' + ing + '</p>' +
                '</div>'
              );
            })
            .join('');
        }
      }
    }
  );
}
